// 👤 API de Usuarios
import { apiClient } from './client';
import type { User } from './auth.api';

export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  perfil_id: string; // UUID del perfil
  empresaId: string;
}

export interface UpdateUserData {
  username?: string;
  email?: string;
  password?: string;
  perfil_id?: string; // UUID del perfil
  activo?: boolean;
}

export interface UsersResponse {
  users: User[];
  total: number;
}

// Selección con embedding de PostgREST: perfil/empresa aliaseados como espera el tipo User
const USER_SELECT =
  'id,username,email,empresa_id,activo,ultimo_acceso,' +
  'perfil:perfiles(id,nombre,descripcion,permisos,nivel_acceso),' +
  'empresa:empresas(id,nombre,logo)';

export const usersAPI = {
  /**
   * Listar usuarios (tabla autenticacion.usuarios; RLS acota a la empresa)
   */
  getAll: async (): Promise<UsersResponse> => {
    const { data } = await apiClient.get(
      `/rest/autenticacion.usuarios?select=${USER_SELECT}&order=created_at.desc`
    );
    return { users: data as User[], total: (data as User[]).length };
  },

  /**
   * Obtener usuario por ID
   */
  getById: async (id: string): Promise<{ user: User }> => {
    const { data } = await apiClient.get(
      `/rest/autenticacion.usuarios?id=eq.${id}&select=${USER_SELECT}&limit=1`
    );
    return { user: (data as User[])[0] };
  },

  /**
   * Crear usuario → /rpc/create_user (hashea la contraseña en la BD)
   */
  create: async (userData: CreateUserData) => {
    const { data } = await apiClient.post('/rpc/create_user', {
      _username: userData.username,
      _email: userData.email,
      _password: userData.password,
      _perfil_id: userData.perfil_id,
    });
    return data;
  },

  /**
   * Actualizar usuario → /rpc/update_user
   */
  update: async (id: string, userData: UpdateUserData) => {
    const { data } = await apiClient.post('/rpc/update_user', {
      _id: id,
      _username: userData.username ?? null,
      _email: userData.email ?? null,
      _password: userData.password ?? null,
      _perfil_id: userData.perfil_id ?? null,
      _activo: userData.activo ?? null,
    });
    return data;
  },

  /**
   * Eliminar usuario (soft delete) → /rpc/delete_user
   */
  delete: async (id: string) => {
    const { data } = await apiClient.post('/rpc/delete_user', { _id: id });
    return data;
  },

  /**
   * Centros de costo asignados a un usuario (para precargar el formulario)
   */
  getCentrosCosto: async (id: string): Promise<Array<{ id: string; razon_social: string }>> => {
    const { data } = await apiClient.post('/rpc/get_usuario_centros_costo', { _usuario_id: id });
    return (data as Array<{ id: string; razon_social: string }>) || [];
  },

  /**
   * Reemplaza los centros de costo asignados a un usuario
   */
  setCentrosCosto: async (id: string, partnerIds: string[]) => {
    const { data } = await apiClient.post('/rpc/set_usuario_centros_costo', {
      _usuario_id: id, _partner_ids: partnerIds,
    });
    return data;
  },
};
